import Check from "@mui/icons-material/Check";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { hasRegistrationUpdateMetadata } from "encamp-shared/src/submissions/wasteStreamRegistration";
import {
  WasteClassification,
  StateWasteStreamRegistrationStatus,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  registrationUpdateTarget,
  useWasteStreamRegistrationUpdateDialog,
} from "routes/Customer/Waste/useWasteStreamRegistrationUpdateDialog";
import {
  CreateWasteStreamRegistrationSubmissionsDialog,
  RegistrationStream,
} from "routes/Customer/Waste/WasteStreams/CreateWasteStreamRegistrationSubmissionsDialog";

import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../../../WasteStreamAmountCell";
import {
  WasteStreamRegistrationCell,
  WasteStreamStateIdCell,
} from "../../../WasteStreamRegistrationCell";
import { profileNumberValueGetter } from "../formatWasteStreamProfileNumber";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import {
  prettyPrintWasteClassification,
  prettyPrintWasteStreamLabel,
} from "../prettyPrintWasteClassification";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamDetailsModal } from "../WasteStreamDetails/WasteStreamDetailsModalV1";
import { useWasteStreamSelection } from "./useWasteStreamSelection";
import { WasteStreamExportDropdown } from "./WasteStreamExportDropdown";
import { WasteStreamMergeDialog } from "./WasteStreamMerge/WasteStreamMergeDialog";
import { WASTE_STREAMS } from "./WasteStreamsDataGrid";

type WasteStreamTileRow = NonNullable<
  NonNullable<WasteStreamsQuery["facility"]>["wasteStreams"]
>["items"][number];

// Give each column a fixed initial width derived from its old `flex` weight so
// the grid overflows and scrolls horizontally instead of squishing columns to
// fit. minWidth stays low (the MUI default) so columns remain resizable to well
// under the initial width.
const COLUMN_WIDTH_PER_FLEX = 280;
const MIN_RESIZE_WIDTH = 50;

// The shared DataGrid shows a pointer cursor on row hover; with row click
// disabled in readOnly mode that would falsely signal clickability, so put the
// default cursor back. Module-level constant so `gridSx` identity is stable.
const READ_ONLY_GRID_SX = {
  "& .MuiDataGrid-row:hover": { cursor: "default" },
};

interface WasteStreamsDataGridProps {
  facilityId: string;
  /**
   * Strips every edit affordance — row-click details modal (and its staff edit
   * dialog), staff merge selection/button, and the registration-submission cell
   * actions — leaving a review-only grid. Search, sort, filters, and export
   * remain. Used by the waste submission stepper, where edits must happen on the
   * waste facility page instead of inline.
   */
  readOnly?: boolean;
}

export const WasteStreamsDataGrid = ({ facilityId, readOnly }: WasteStreamsDataGridProps) => {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { data } = useWasteFacility(facilityId);
  const { selectedRows, setSelectedRows, isRowSelectable, canMergeSelected } =
    useWasteStreamSelection<WasteStreamTileRow>();

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId", {
    prefixSegment: "wasteStream",
  });
  const { pushIdToUrl: pushLineItemIdToUrl } = useSelectedIdInUrl("wasteLineUnifiedId");

  const handleRowClick = (params: GridRowParams<WasteStreamTileRow>) => {
    pushIdToUrl(params.row.id);
  };

  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { omnisearch } = useOmnisearchInput({ isURLDriven: true });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [, setQueryData] = useState<WasteStreamsQuery | undefined>(undefined);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [registrationStreams, setRegistrationStreams] = useState<RegistrationStream[]>([]);

  const facilityState = data?.facility?.state ?? null;
  const showRegistrationColumn = useShowWasteStateRegistration(facilityState);

  const openRegistrationDialog = useCallback((streams: RegistrationStream[]) => {
    setRegistrationStreams(streams);
    setRegistrationDialogOpen(true);
  }, []);

  const { openUpdateDialog, updateSubmissionDialog } = useWasteStreamRegistrationUpdateDialog(
    useCallback(
      (getSubmissionId: () => Promise<string | undefined>) => {
        // An in-app navigation, not a new tab — no popup blocker to dodge, so
        // no need to reserve anything ahead of the await.
        void (async () => {
          const submissionId = await getSubmissionId();
          if (submissionId) {
            navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
          }
        })();
      },
      [navigate, tenantId]
    )
  );

  const exportFilename = useMemo(
    () => (data.facility?.name ? `${data.facility.name}-waste-streams` : undefined),
    [data.facility?.name]
  );

  const columns: OmnisearchGridColDef<WasteStreamTileRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<WasteStreamTileRow>[] = [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  title={params.value}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.value}
                </Typography>
                {params.row.willNotReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not Reportable
                  </Typography>
                )}
              </Box>
              {isStaff && <MergedWasteStreamChip mergeCount={params.row.mergeCount} />}
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
            {!readOnly && <ArrowRightIcon sx={{ flexShrink: 0, fontSize: 16 }} />}
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.5,
        filterable: false,
        sortable: true,
        valueFormatter: (_, row) => prettyPrintWasteStreamLabel(row.classification, row.wasteKind),
        headerClassName: "wrapHeader",
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "managementMethodCodes",
        headerName: "Management Method Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          return row.managementMethodCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        // Canonical effective amount with a per-source tooltip. Not
        // filterable: effectiveAmount is a server aggregate.
        field: "effectiveAmount",
        headerName: "Amount",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        renderHeader: () => <WasteStreamAmountHeader />,
        renderCell: (params) => (
          <WasteStreamAmountCell
            amount={params.row.effectiveAmount}
            vendorAmount={params.row.vendorAmount}
            eManifestAmount={params.row.eManifestAmount}
            documentAmount={params.row.documentAmount}
          />
        ),
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumn) {
      base.splice(
        2,
        0,
        {
          // FK-backed State ID column, mirroring the v2 grid. Display-only.
          field: "stateRegisteredId",
          headerName: "State ID",
          sortable: false,
          filterable: false,
          flex: 0.4,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamStateIdCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
            />
          ),
        },
        {
          // FK-backed Registration column, mirroring the v2 grid: the status
          // chip + flags. Non-staff (the only audience for this v1 grid, since
          // staff always resolve to v2 via the ManifestViewsV2 staff override)
          // see the chip only; the staff-only affordances below are inert here.
          field: "registrationDisplayStatus",
          headerName: "Registration",
          // Not sortable — same reason as the v2 grid: the status is derived in
          // a page-scoped CTE, after ORDER BY / LIMIT have already chosen the
          // page, so a sortable header would bind to nothing and silently fall
          // back to the default name sort.
          sortable: false,
          filterable: false,
          flex: 0.8,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamRegistrationCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
              openSubmissionId={row.openRegistrationSubmission?.id}
              onOpenRegistration={() => {
                /* No registration detail drawer in the v1 grid. */
              }}
              onOpenDialog={() => {
                if (readOnly) return;
                openRegistrationDialog([
                  {
                    id: row.id,
                    name: row.name,
                    facilityName: data.facility?.name ?? "—",
                    state: facilityState ?? "",
                  },
                ]);
              }}
              onOpenSubmission={
                readOnly
                  ? undefined
                  : (submissionId) =>
                      navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`)
              }
              flags={row.registrationFlags}
              openSubmissionIsUpdate={hasRegistrationUpdateMetadata(
                row.openRegistrationSubmission?.metadata
              )}
              onCreateUpdateSubmission={
                readOnly
                  ? undefined
                  : () =>
                      openUpdateDialog(
                        registrationUpdateTarget(
                          row,
                          {
                            id: facilityId,
                            name: data.facility?.name ?? "—",
                            state: facilityState ?? "",
                          },
                          { startDate: deferredFilters.startDate, endDate: deferredFilters.endDate }
                        )
                      )
              }
            />
          ),
        }
      );
    }
    return base.map(({ flex, ...rest }) => ({
      ...rest,
      width: rest.width ?? Math.round((flex ?? 1) * COLUMN_WIDTH_PER_FLEX),
      minWidth: MIN_RESIZE_WIDTH,
    }));
  }, [
    isStaff,
    readOnly,
    showRegistrationColumn,
    facilityState,
    openRegistrationDialog,
    data.facility?.name,
    navigate,
    tenantId,
    facilityId,
    openUpdateDialog,
    deferredFilters.startDate,
    deferredFilters.endDate,
  ]);

  return (
    <>
      <OmnisearchDataGrid<WasteStreamsQuery, WasteStreamsQueryVariables, WasteStreamTileRow>
        persistenceKey="waste-streams-table-v1"
        isURLDriven={true}
        columns={columns}
        dataQuery={WASTE_STREAMS}
        getItems={(data) => data.facility?.wasteStreams?.items ?? []}
        getCount={(data) => data.facility?.wasteStreams?.count ?? 0}
        dataQueryVariables={{
          facilityId,
          startDate: deferredFilters.startDate,
          endDate: deferredFilters.endDate,
          view: deferredFilters.view,
          tags: deferredFilters.tags,
        }}
        initialPageSize={10}
        withPadding={false}
        defaultSearch=""
        noDataMessage={"No Waste Streams found for this facility."}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        onRowClick={readOnly ? undefined : handleRowClick}
        gridSx={readOnly ? READ_ONLY_GRID_SX : undefined}
        disableRowSelectionOnClick={true}
        onDataStateChange={(data) => {
          setQueryData(data ?? undefined);
        }}
        commandButtons={[
          isStaff && !readOnly ? (
            <Button
              key="merge-waste-streams-button"
              size="small"
              variant="contained"
              sx={{
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.warning.light,
              }}
              endIcon={<ArrowRightIcon />}
              disabled={!canMergeSelected}
              title={`Merge selected waste streams`}
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge Waste Streams
            </Button>
          ) : null,
          <WasteStreamExportDropdown
            key="export-waste-streams-button"
            isStaff={isStaff}
            disabled={!facilityId}
            facilityId={facilityId}
            tenantId={tenantId ?? ""}
            fileName={exportFilename}
            filter={{
              search: omnisearch,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
            }}
            dataQuery={WASTE_STREAMS}
            dataQueryVariables={{
              facilityId,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
              search: omnisearch || undefined,
            }}
            onWasteStreamClick={pushIdToUrl}
          />,
        ]}
        selectedIds={selectedRows.map((row) => row.id)}
        onSelectedRowsChanged={isStaff && !readOnly ? setSelectedRows : undefined}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isStaff && !readOnly}
        disableSelectAll
        additionalFilterColumns={[
          {
            filterKeyType: "enumAutocomplete",
            key: "classification",
            header: "Classification",
            enumValues: Object.values(WasteClassification),
            enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
          },
          ...(showRegistrationColumn
            ? [
                {
                  filterKeyType: "boolean" as const,
                  key: "needsStateRegistration",
                  header: "Needs State Registration",
                },
                {
                  filterKeyType: "enumAutocomplete" as const,
                  key: "stateRegistrationStatus",
                  header: "Registration Status",
                  enumValues: Object.values(StateWasteStreamRegistrationStatus),
                  ignoreStateAbbreviations: true,
                },
              ]
            : []),
        ]}
      />

      {isMergeDialogOpen && (
        <WasteStreamMergeDialog
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedRows([]); // Clear selections when dialog closes
          }}
          selectedRows={selectedRows}
        />
      )}

      {updateSubmissionDialog}

      {registrationDialogOpen && (
        <CreateWasteStreamRegistrationSubmissionsDialog
          open
          onClose={() => setRegistrationDialogOpen(false)}
          tenantId={tenantId ?? ""}
          streams={registrationStreams}
          onSuccess={() => {}}
        />
      )}

      {/* readOnly also guards deep links: `wasteStreamId` in the URL would
          otherwise open the modal (and its staff edit dialog) without a click. */}
      {!readOnly && selectedId && (
        <WasteStreamDetailsModal
          open={!!selectedId}
          onClose={() => popIdFromUrl()}
          wasteStreamId={selectedId}
          // Nest under the open stream so the facility page keeps the stream
          // drawer mounted (same pattern as TenantWasteStreams). The old
          // `../manifest-line-items/...` href left the facility page for the
          // standalone line-items route and closed the stream.
          buildLineItemHref={(lineItemId) =>
            `/o/${tenantId}/waste/facilities/${facilityId}/wasteStream/${selectedId}/${lineItemId}`
          }
          onLineItemClick={pushLineItemIdToUrl}
        />
      )}
    </>
  );
};
